import {Suspense} from 'react'
import {useLazyLoadQuery} from 'react-relay'
import {graphql} from 'relay-runtime'
import {Banner} from '@primer/react/experimental'
import {PAGE_SIZE, RelationshipsListAllView, RelationshipsListViewLoadingSkeleton} from './RelationshipsListAllView'
import type {LazyRelationshipsBlockedByListViewQuery} from './__generated__/LazyRelationshipsBlockedByListViewQuery.graphql'
import {LABELS} from '../../../constants/labels'

export const LazyRelationshipsBlockedByListViewQueryGraphQL = graphql`
  query LazyRelationshipsBlockedByListViewQuery($id: ID!) {
    issue: node(id: $id) {
      ... on Issue {
        repository {
          nameWithOwner
        }
        blockedBy(first: 100) {
          nodes {
            ...RelationshipsListAllViewItemFragments
          }
        }
      }
    }
  }
`

export type LazyRelationshipsBlockedByListViewProps = {
  itemId: string
  totalItems?: number
}
export function LazyRelationshipsBlockedByListView({itemId, totalItems = -1}: LazyRelationshipsBlockedByListViewProps) {
  // TODO: when issueDependenciesSummary totals are available, remove the default -1 behavior altogether
  if (totalItems === -1) totalItems = Math.floor(PAGE_SIZE / 2)
  const loadingSkeletonComponent = <RelationshipsListViewLoadingSkeleton totalItems={totalItems} />

  return (
    <Suspense fallback={loadingSkeletonComponent}>
      <RelationshipsBlockedByListView itemId={itemId} />
    </Suspense>
  )
}

type RelationshipsBlockedByListViewProps = {
  itemId: string
}
function RelationshipsBlockedByListView({itemId}: RelationshipsBlockedByListViewProps) {
  const queryData = useLazyLoadQuery<LazyRelationshipsBlockedByListViewQuery>(
    LazyRelationshipsBlockedByListViewQueryGraphQL,
    {id: itemId},
  )
  const emptyBanner = (
    <Banner variant="info">
      <Banner.Title>{LABELS.emptyBlockedByList}</Banner.Title>
    </Banner>
  )

  const {issue} = queryData
  if (!issue) return emptyBanner

  const repository = issue.repository
  if (!repository) return emptyBanner

  const issueFragments = issue.blockedBy?.nodes?.filter(issueFragment => !!issueFragment)
  if (!issueFragments || issueFragments.length === 0) return emptyBanner

  return (
    <RelationshipsListAllView
      accessibleTitle={LABELS.relationNames.blockedByIssues}
      repositoryNameWithOwner={repository.nameWithOwner}
      issueFragments={issueFragments}
    />
  )
}

try{ LazyRelationshipsBlockedByListView.displayName ||= 'LazyRelationshipsBlockedByListView' } catch {}
try{ RelationshipsBlockedByListView.displayName ||= 'RelationshipsBlockedByListView' } catch {}